import axios from '../../helpers/bearerAxios';
import { logger } from '../loggingService';

const baseUrl = import.meta.env.VITE_ONEBUTTON_CONTRACT_API_URL || '';
const contractBaseUrl =
    import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL || baseUrl.replace(/\/api\/Contract.*$/i, '') || '';

const sunlightApiRoot = (): string => {
    const env = String(import.meta.env.VITE_ONEBUTTON_SUNLIGHT_API_URL || '').replace(/\/$/, '');
    if (env) return env;
    return `${String(contractBaseUrl || '').replace(/\/$/, '')}/api/sunlight`;
};

const goodLeapApiRoot = (): string => {
    const root = String(contractBaseUrl || '').replace(/\/$/, '');
    if (!root) return '';
    return `${root}/api/goodLeap`;
};

/** Trinity `GenerateContractService.GetSigningRecords` — before Combined Pack signing. */
const getSigningRecords = async (financePartnerQuoteId: string | number, signingMethod: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetSigningRecords?financePartnerQuoteId=${encodeURIComponent(String(financePartnerQuoteId))}&signingMethod=${encodeURIComponent(signingMethod)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.CheckDuplicateEmails` — In-Person signing (`opportunityId` may be unset like Angular `masterService.opportunityId`). */
const checkDuplicateEmails = async (opportunityId: string | number | null | undefined) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CheckDuplicateEmails?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.GetAgreementDetails` — email signing last-event date. */
const getAgreementDetails = async (agreementId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetAgreementDetails?agreementId=${encodeURIComponent(agreementId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

export type GetApplicationStatusPayload = {
    opportunityId: string | number;
    financePartnerContractIds: (string | number)[];
};

/** Trinity `GenerateContractService.getApplicationStatus` — batch Service Finance signed status. */
const getApplicationStatus = async (payload: GetApplicationStatusPayload) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetApplicationStatus`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.updateIsReadyToSign` — contract grid “Ready to Sign” checkbox. */
const updateIsReadyToSign = async (quoteId: string | number, isReadyToSign: boolean) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdateIsReadyToSign?quoteId=${encodeURIComponent(String(quoteId))}&isReadyToSign=${encodeURIComponent(String(isReadyToSign))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.GetRoofingProposalURL` — roof / solar / battery proposal link. */
const getRoofingProposalURL = async (financePartnerQuoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetRoofingProposalURL?financePartnerQuoteId=${encodeURIComponent(String(financePartnerQuoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.submitApplication` — Service Finance primary/co-applicant payload array. */
const submitServiceFinanceApplication = async (payload: unknown[]) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/SubmitApplication`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `getServiceFincaeContractStatus` — single-row Service Finance contract refresh. */
const getOpportunityContractStatus = async (opportunityId: string | number, contractId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/OpportunityContractStatus?opportunityId=${encodeURIComponent(String(opportunityId))}&contractId=${encodeURIComponent(String(contractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.LightReachCreateQuote` — agreements grid “Submit Quote”. */
const lightReachCreateQuote = async (opportunityId: string | number, opportunityContractId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/LightReachCreateQuote?opportunityId=${encodeURIComponent(String(opportunityId))}&opportunityContractId=${encodeURIComponent(String(opportunityContractId))}`;
        const response = await axios.post(url, null);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `getFinancePartnerContractStatus` / `GetLRFormStatus` — Lightreach form refresh. */
const getLightreachFormStatus = async (opportunityId: string | number, contractId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetLRFormStatus?opportunityId=${encodeURIComponent(String(opportunityId))}&contractId=${encodeURIComponent(String(contractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.credit` — Sunlight loan application step (returns iframe URL in `Data`). */
const sunlightCredit = async (opportunityID: string | number, quoteID: string | number, inPerson: string) => {
    try {
        const root = sunlightApiRoot();
        const url = `${root}/credit?opportunityID=${encodeURIComponent(String(opportunityID))}&quoteID=${encodeURIComponent(String(quoteID))}&inPerson=${encodeURIComponent(inPerson)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.SendLoanDocument` — Sunlight send loan docs. */
const sunlightSendLoanDocument = async (syncLeadId: string | number, inPerson: string) => {
    try {
        const root = sunlightApiRoot();
        const url = `${root}/SendLoanDocument?syncLeadId=${encodeURIComponent(String(syncLeadId))}&inPerson=${encodeURIComponent(inPerson)}`;
        const response = await axios.post(url, {});
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.generateProposal` — roof proposal email / Adobe signing. */
const generateProposal = async (params: {
    opportunityId: number;
    quoteID: number;
    productType: string;
    manualOverride?: boolean;
    isEmail: boolean;
    showFinancialsforAdders?: boolean;
}) => {
    try {
        const q = new URLSearchParams({
            opportunityId: String(params.opportunityId),
            quoteID: String(params.quoteID),
            productType: params.productType,
            ManualOverride: String(!!params.manualOverride),
            isEmail: String(params.isEmail),
            showFinancialsforAdders: String(!!params.showFinancialsforAdders),
        });
        const url = `${contractBaseUrl}/api/Contract/GenerateProposal?${q.toString()}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.createDocumateSunnovaAPI` — View Solar/Battery proposal (Sunnova). */
const createDocumateSunnovaAPI = async (params: {
    quoteId: string | number;
    agreementType: string;
    isEmail: boolean;
    mergeDocuments: boolean;
    opportunityId: string | number;
}) => {
    try {
        const q = new URLSearchParams({
            quoteId: String(params.quoteId),
            agreementType: String(params.agreementType ?? ''),
            IsEmail: String(params.isEmail),
            MergeDocuments: String(params.mergeDocuments),
            opportunityId: String(params.opportunityId),
        });
        const url = `${contractBaseUrl}/api/Contract/CreateDocumateSunnovaAPI?${q.toString()}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.updateSystemDesign` — resync System Design stage. */
const goodLeapUpdateSystemDesign = async (opportunityId: string | number, loanId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/updateSystemDesign?OpportunityId=${encodeURIComponent(String(opportunityId))}&LoanId=${encodeURIComponent(String(loanId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.updateUtility` — resync Utility stage. */
const goodLeapUpdateUtility = async (opportunityId: string | number, loanId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/updateUtility?OpportunityId=${encodeURIComponent(String(opportunityId))}&LoanId=${encodeURIComponent(String(loanId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.sendLoanDocument` — resend documents stage. */
const goodLeapSendLoanDocument = async (opportunityId: string | number, loanId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/sendLoanDocument?OpportunityId=${encodeURIComponent(String(opportunityId))}&LoanId=${encodeURIComponent(String(loanId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.CreateEnfinLoanApplcation`. */
const createEnfinLoanApplication = async (payload: {
    opportunityId: string | number;
    contractId: string | number;
    co?: boolean;
}) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CreateEnfinLoanApplcation`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.createEnfinContract` — Enfin System Design resync. */
const createEnfinContract = async (payload: {
    opportunityId: string | number;
    applicationId: string | number;
    quoteId: string | number;
}) => {
    try {
        const base_url = import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL
        const url = `${base_url}/api/Contract/CreateEnfinContract`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.cancelEnfinApplication`. */
const cancelEnfinApplication = async (payload: {
    opportunityId: string | number;
    applicationNo: string | number;
    cancelReason: string;
}) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CancelEnfinLoanApplcation`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.cancelApplicationProcess` — GoodLeap cancel loan application. */
const goodLeapCancelLoanApplication = async (opportunityId: string | number, loanId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/CancelLoanApplication?opportunityId=${encodeURIComponent(String(opportunityId))}&LoanId=${encodeURIComponent(String(loanId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.getTimelineData` — GoodLeap timeline. */
const goodLeapGetTimeline = async (loanId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/Timeline?loanid=${encodeURIComponent(String(loanId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.getPpaSignedProductsStatus` — get product-level signed status */
const getPpaSignedProductsStatus = async (opportunityId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetPPASignedProductsStatus?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.updatePpaSignedProducts` — set product-level signed status */
const updatePpaSignedProducts = async (payload: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdatePPASignedProductsStatus`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.cancelIGSApplicationProcess` — IGS signed-contract cancel from agreements detail. */
const cancelIGSApplication = async (partnerSystemNumber: string | number, opportunityContractId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CancelIGSApplication?partnerSystemNumber=${encodeURIComponent(String(partnerSystemNumber))}&opportunityContractId=${encodeURIComponent(String(opportunityContractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.ApplicationCreditStatus` — GoodLeap “Get Credit Status”. */
const goodLeapApplicationCreditStatus = async (
    opportunityId: string | number,
    applicationID: string | number,
    opportunityContractID: string | number,
) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/ApplicationCreditStatus?opportunityId=${encodeURIComponent(String(opportunityId))}&applicationID=${encodeURIComponent(String(applicationID))}&opportunityContractID=${encodeURIComponent(String(opportunityContractID))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: unknown) {
        logger.error(error);
        throw error;
    }
};

const getFinanceSelection = async (opportunityId: string) => {
    try {
        const url = `${baseUrl}/GetFinanceSelection?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const contractService = {
    getSigningRecords,
    checkDuplicateEmails,
    getAgreementDetails,
    updateIsReadyToSign,
    getApplicationStatus,
    getRoofingProposalURL,
    submitServiceFinanceApplication,
    getOpportunityContractStatus,
    getPpaSignedProductsStatus,
    updatePpaSignedProducts,
    lightReachCreateQuote,
    getLightreachFormStatus,
    sunlightCredit,
    sunlightSendLoanDocument,
    generateProposal,
    createDocumateSunnovaAPI,
    goodLeapUpdateSystemDesign,
    goodLeapUpdateUtility,
    goodLeapSendLoanDocument,
    createEnfinLoanApplication,
    createEnfinContract,
    cancelEnfinApplication,
    goodLeapCancelLoanApplication,
    goodLeapGetTimeline,
    goodLeapApplicationCreditStatus,
    cancelIGSApplication,
    getFinanceSelection
};

export default contractService;
